import { isHiddenField, labelForField } from './companyFieldLabels';

/**
 * Rendering one side of a company change (JBWEB-1211).
 *
 * The history stores whatever the field held — a percentage, a sub-document, a
 * list of night-hour ranges, a whole coefficient grid — so this turns any of
 * them into lines the Activité section can print. Lines rather than a string:
 * the design bullets a rate table and prints a single value plainly, and only
 * the number of lines separates the two.
 */

const isEmpty = (value) =>
  value === null ||
  value === undefined ||
  value === '' ||
  (Array.isArray(value) && value.length === 0);

const asPercentage = (value) =>
  value === null || value === undefined ? null : `${value} %`;

/** A single entry of a rate list, as the cards phrase it elsewhere. */
const formatRate = (rate) => {
  const percentage = asPercentage(rate.percentage) ?? 'Non défini';

  // Night hours carry a window, overtime a threshold: the shape says which.
  if (rate.start !== undefined && rate.end !== undefined) {
    return `Entre ${rate.start}h et ${rate.end}h : ${percentage}`;
  }
  if (rate.start !== undefined) {
    return `Plus de ${rate.start}h sur une semaine : ${percentage}`;
  }
  return percentage;
};

/**
 * A coefficient range, phrased as the grid shows it.
 *
 * The percentage is stored above the minimum wage, which is what the grid is
 * built on and not what anyone reads: given the wage, the ceiling is shown in
 * euros. Prefixed with ">" like the rows of the coefficients page, and without
 * the wage itself as a lower bound — it is the same on every line and would
 * cost the width the coefficients need.
 */
const formatRange = (range, minimumWage) =>
  [
    range.percentage !== null && range.percentage !== undefined
      ? minimumWage
        ? `> ${(minimumWage * (1 + range.percentage / 100)).toFixed(2)} €`
        : `+${range.percentage} %`
      : null,
    range.delegation !== null && range.delegation !== undefined
      ? `délégation ${range.delegation}`
      : null,
    range.management !== null && range.management !== undefined
      ? `gestion ${range.management}`
      : null,
  ]
    .filter(Boolean)
    .join(' · ');

const formatObject = (value) => {
  if (value.percentage !== undefined && Object.keys(value).length <= 3) {
    // sundayHours / holidayHours: a rate, sometimes with a solidarity day.
    return [
      asPercentage(value.percentage) ?? 'Non défini',
      value.solidarityDay ? `Jour de solidarité : ${value.solidarityDay}` : null,
    ].filter(Boolean);
  }

  if (value.employer_share !== undefined) {
    // The restaurant ticket, phrased as the card phrases it.
    return [
      value.value !== undefined && value.value !== null
        ? `Ticket de ${value.value} € (${value.employer_share} % part patronale)`
        : `Part patronale : ${value.employer_share} %`,
    ];
  }

  if (value.delegation !== undefined || value.management !== undefined) {
    return [formatRange(value)];
  }


  if (value.street !== undefined || value.city !== undefined) {
    return [
      [value.street, value.complement, [value.postcode, value.city].filter(Boolean).join(' ')]
        .filter(Boolean)
        .join(', '),
    ];
  }

  // Anything else: name the fields rather than print a JSON blob at someone
  // trying to read what changed.
  // Named in French like the field that holds them: a box titled "Ticket
  // restaurant" whose lines read "employerShare" explains nothing.
  return Object.entries(value)
    .filter(([key]) => !isHiddenField(key))
    .filter(([, inner]) => !isEmpty(inner))
    .map(
      ([key, inner]) =>
        `${labelForField(key)} : ${formatCompanyActivityValue(inner).join(', ')}`
    );
};

export const formatCompanyActivityValue = (value, minimumWage) => {
  if (isEmpty(value)) return [];

  if (Array.isArray(value)) {
    return value.flatMap((entry) => {
      if (entry && typeof entry === 'object') {
        if (entry.start !== undefined || entry.percentage !== undefined) {
          if (entry.delegation !== undefined || entry.management !== undefined) {
            return [formatRange(entry, minimumWage)];
          }
          return [formatRate(entry)];
        }
        return formatObject(entry);
      }
      return [String(entry)];
    });
  }

  if (typeof value === 'object') return formatObject(value);
  if (typeof value === 'boolean') return [value ? 'Oui' : 'Non'];
  return [String(value)];
};

export default formatCompanyActivityValue;
